import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaArrowRight,
  FaHandshake,
  FaRegCalendarAlt,
  FaGlobeEurope,
  FaFilePdf,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { testUrl } from "../BaseData";
import classes from "./GrantlarVaLoyihalar.module.css";
import {
  getLabels,
  pick,
  formatDate,
  flagUrl,
  imgUrl,
  fixHtml,
  GRANTS_BASE,
  SECTION_PAGES,
  sectionPath,
} from "./grantUtils";
import memorandumImg from "../../../shared/assets/images/grants/grants-memorandum.jpg";
import researchImg from "../../../shared/assets/images/grants/grants-research.jpg";
import exchangeImg from "../../../shared/assets/images/grants/grants-exchange.jpg";

// Rasm yuklanmagan yozuvlar uchun bo'lim rasmi
const FALLBACK_IMAGES = {
  memorandums: memorandumImg,
  research: researchImg,
  exchange: exchangeImg,
};

const itemImage = (item, section) =>
  item.image ? imgUrl(item.image) : FALLBACK_IMAGES[section];

// Davlat belgisi (bayroq + nomi)
const CountryBadge = ({ item, lang }) => {
  const country = pick(item, "country", lang);
  const flag = flagUrl(item.country_code);
  if (!country && !flag) return null;
  return (
    <span className={classes.country}>
      {flag && (
        <img
          src={flag}
          alt=""
          className={classes.flag}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      )}
      {country}
    </span>
  );
};

// Hamkor va sana
const ItemStats = ({ item, lang, L }) => {
  const partner = pick(item, "partner", lang);
  if (!partner && !item.date) return null;
  return (
    <div className={classes.stats}>
      {partner && (
        <div className={classes.stat}>
          <span className={`${classes.statIcon} ${classes.statIconFunding}`}>
            <FaHandshake />
          </span>
          <div className={classes.statText}>
            <span className={classes.statLabel}>{L.partner}</span>
            <span className={classes.statValue} title={partner}>
              {partner}
            </span>
          </div>
        </div>
      )}
      {item.date && (
        <div className={classes.stat}>
          <span className={`${classes.statIcon} ${classes.statIconDeadline}`}>
            <FaRegCalendarAlt />
          </span>
          <div className={classes.statText}>
            <span className={classes.statLabel}>{L.date}</span>
            <span className={classes.statValue}>{formatDate(item.date, lang)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionCard = ({ item, section, lang, L }) => {
  const navigate = useNavigate();
  const title = pick(item, "title", lang);
  const short = pick(item, "short", lang);
  const path = sectionPath(section, item.id);

  return (
    <article
      className={classes.card}
      onClick={() => navigate(path)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(path)}
    >
      <div className={classes.media}>
        <img src={itemImage(item, section)} alt={title} className={classes.image} loading="lazy" />
        <div className={classes.mediaShade} />
        <CountryBadge item={item} lang={lang} />
      </div>

      <div className={classes.body}>
        <h3 className={classes.title} title={title}>
          {title}
        </h3>
        {short && <p className={classes.sectionItemShort}>{short}</p>}
        <ItemStats item={item} lang={lang} L={L} />
        <div className={classes.cardFooter}>
          <span className={classes.more}>
            {L.details} <FaArrowRight />
          </span>
        </div>
      </div>
    </article>
  );
};

/**
 * Bo'lim ro'yxati: /activity/ilmiy-faoliyat/grantlar-va-loyihalar/<memorandumlar|ilmiy-tadqiqotlar|akademik-almashinuv>
 * Ma'lumot admin paneldan: Grantlar, loyihalar, memorandumlar bo'limi
 */
export const GrantSectionList = ({ section }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const L = getLabels(lang);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${testUrl}/api/grant-sections/${section}/`)
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError(L.error))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  return (
    <div className={classes.page}>
      <Link to={GRANTS_BASE} className={classes.back}>
        <FaArrowLeft /> {L.backToSections}
      </Link>
      <h1 className={classes.pageTitle}>{L[SECTION_PAGES[section].label]}</h1>

      {loading ? (
        <div className={classes.status}>{L.loading}</div>
      ) : error ? (
        <div className={classes.status}>{error}</div>
      ) : items.length === 0 ? (
        <div className={classes.status}>{L.emptySection}</div>
      ) : (
        <div className={classes.grid}>
          {items.map((item) => (
            <SectionCard key={item.id} item={item} section={section} lang={lang} L={L} />
          ))}
        </div>
      )}
    </div>
  );
};

/** Bo'lim yozuvi batafsil: .../<bo'lim>/<id> */
export const GrantSectionDetail = ({ section }) => {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const L = getLabels(lang);

  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${testUrl}/api/grant-sections/${section}/${id}/`)
      .then((res) => {
        setItem(res.data.item || null);
        setRelated(res.data.related || []);
      })
      .catch((err) => setError(err?.response?.status === 404 ? L.notFound : L.error))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, id]);

  if (loading) return <div className={classes.status}>{L.loading}</div>;
  if (error) return <div className={classes.status}>{error}</div>;
  if (!item) return <div className={classes.status}>{L.notFound}</div>;

  const title = pick(item, "title", lang);
  const country = pick(item, "country", lang);
  const content = pick(item, "content", lang) || pick(item, "short", lang);
  const flag = flagUrl(item.country_code);
  const image = itemImage(item, section);

  return (
    <div className={classes.detail}>
      <Link to={sectionPath(section)} className={classes.back}>
        <FaArrowLeft /> {L[SECTION_PAGES[section].label]}
      </Link>

      <div className={classes.detailCard}>
        <div className={classes.detailImageWrap}>
          <div className={classes.detailImageBg} style={{ backgroundImage: `url(${image})` }} />
          <img src={image} alt={title} className={classes.detailImage} />
          <CountryBadge item={item} lang={lang} />
        </div>

        <div className={classes.detailBody}>
          <h1 className={classes.detailTitle}>{title}</h1>

          {(country || item.date || pick(item, "partner", lang)) && (
          <div className={classes.detailMeta}>
            <ItemStats item={item} lang={lang} L={L} />
            {country && (
              <div className={classes.stat}>
                <span className={`${classes.statIcon} ${classes.statIconCountry}`}>
                  <FaGlobeEurope />
                </span>
                <div className={classes.statText}>
                  <span className={classes.statLabel}>{L.country}</span>
                  <span className={`${classes.statValue} ${classes.statRow}`}>
                    {flag && (
                      <img
                        src={flag}
                        alt=""
                        className={classes.flag}
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                    {country}
                  </span>
                </div>
              </div>
            )}
          </div>
          )}

          {content && (
            <div className={classes.content} dangerouslySetInnerHTML={{ __html: fixHtml(content) }} />
          )}

          <div className={classes.sectionItemActions}>
            {item.file && (
              <a
                href={imgUrl(item.file)}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.applyBtn}
              >
                {L.download} <FaFilePdf />
              </a>
            )}
            {item.link && (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.applyBtn}
              >
                {L.visitLink} <FaExternalLinkAlt />
              </a>
            )}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <>
          <h2 className={classes.relatedTitle}>{L.otherItems}</h2>
          <div className={classes.grid}>
            {related.map((r) => (
              <SectionCard key={r.id} item={r} section={section} lang={lang} L={L} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
